import React from 'react'
import styled from 'styled-components'
import { ReactComponent as DownloadIcon } from 'images/download-arrow.svg'
import { ReactComponent as UploadIcon } from 'images/upload-arrow.svg'
import { ReactComponent as PingIcon } from 'images/ping-arrow.svg'
import SpeedData from 'src/Home/SpeedMeter/SpeedData'
import { SpeedTestState } from 'src/Common/SpeedTestState'
import { useTheme } from 'src/Styles/theme'
import { SpeedResultState } from 'src/Common/SpeedResultState'
import { localize } from 'src/lib/localize'
import { UPLOAD, DOWNLOAD, PING } from 'src/Common/Constant'

const MainContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-contain: center;
  align-items: center;
`
const Container = styled.div<{ hideMobileView: boolean }>`
  display: flex;
  flex: 1;
  flex-direction: row;
  width: 100%;
  justify-contain: space-around;

  @media (min-width: 320px) {
    margin-top: 24px;
    display: ${({ hideMobileView }) => (hideMobileView ? 'none' : 'flex')};
  }

  @media (min-width: 768px) {
    margin-top: 24px;
    display: flex;
    min-width: 400px;
  }

  @media (min-width: 1024px) {
    margin-top: 32px;
    display: flex;
  }
`

const downloadToolTipText = localize('LABEL')(
  'Download speed is the rate at which data is<br />transferred from the internet to your device.'
)
const uploadToolTipText = localize('LABEL')(
  'Upload speed is the rate at which data is<br />transferred from your device to the internet.'
)
const pingToolTipText = localize('LABEL')(
  'Ping is the amount of time it takes for<br />another device on the internet to respond to<br />your device. The lower the ping the better.'
)

interface Props {
  download: string
  upload: string
  ping: string
  testState?: SpeedTestState
  resultState?: SpeedResultState
}

const SpeedCombineData = ({
  download,
  upload,
  ping,
  testState,
  resultState,
}: Props) => {
  const {
    outerRingColor,
    innerRingColor,
    fastOuterRingColor,
    fastInnerRingColor,
    slowOuterRingColor,
    slowInnerRingColor,
    unstableOuterRingColor,
    unstableInnerRingColor,
  } = useTheme().colors

  const getInnerRingColor = () => {
    switch (resultState) {
      case SpeedResultState.FAST:
        return fastInnerRingColor
      case SpeedResultState.SLOW:
        return slowInnerRingColor
      case SpeedResultState.UNSTABLE:
        return unstableInnerRingColor
      default:
        return innerRingColor
    }
  }

  const getOuterRingColor = () => {
    switch (resultState) {
      case SpeedResultState.FAST:
        return fastOuterRingColor
      case SpeedResultState.SLOW:
        return slowOuterRingColor
      case SpeedResultState.UNSTABLE:
        return unstableOuterRingColor
      default:
        return outerRingColor
    }
  }
  const DownloadView = () => {
    return (
      <SpeedData
        dataIcon={DownloadIcon}
        innerRingColor={getInnerRingColor()}
        outerRingColor={getOuterRingColor()}
        speed={download}
        speedDescription={'Mbps'}
        speedType={DOWNLOAD}
        toolTipId={'downloadView'}
        toolTipText={downloadToolTipText}
      />
    )
  }

  const UploadView = () => {
    return (
      <SpeedData
        dataIcon={UploadIcon}
        innerRingColor={getInnerRingColor()}
        outerRingColor={getOuterRingColor()}
        speed={upload}
        speedDescription={'Mbps'}
        speedType={UPLOAD}
        toolTipId={'uploadView'}
        toolTipText={uploadToolTipText}
      />
    )
  }

  const PingView = () => {
    return (
      <SpeedData
        dataIcon={PingIcon}
        innerRingColor={getInnerRingColor()}
        outerRingColor={getOuterRingColor()}
        speed={ping}
        speedDescription={'Ms'}
        speedType={PING}
        toolTipId={'pingView'}
        toolTipText={pingToolTipText}
      />
    )
  }
  return (
    <MainContainer>
      <Container hideMobileView={testState === SpeedTestState.IDEAL}>
        <PingView />
        <DownloadView />
        <UploadView />
      </Container>
    </MainContainer>
  )
}

export default SpeedCombineData
